/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { ofType } from 'redux-observable';
import { SecurityHelper as SecurityUtils } from 'sarsaparilla';
import axios from 'axios';
import { from, of } from 'rxjs';
import {
    map,
    debounceTime,
    withLatestFrom,
    pluck,
    mergeMap,
    catchError,
} from 'rxjs/operators';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import includes from 'lodash/includes';
import set from 'lodash/set';
import find from 'lodash/find';
import lodashMap from 'lodash/map';
import cloneDeep from 'lodash/cloneDeep';
import isNil from 'lodash/isNil';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import {
    feeConstants,
    campgroundConstants,
    constants as types,
    orderDetailsUtil,
} from '../../shared';
import { STANDARD_TYPE_FACILITY } from '../../shared/constants/campground';
import { INTERAGENCY_PASSES } from '../../shared/constants/passes';
import {
    cleanSupplementalSelections,
    updateNightMapOccupants,
} from '../../shared/utils/reservation_utils';

const { ITINERARY_TYPE_FACILITY } = campgroundConstants;
/**
 * @param {object} priceRequestBody - price object containing all necessary reservaiton info to fetch new price
 *
 * Request to fetch most up-to-date reservation price
 */
export function fetchReservationPriceDetails(priceRequestBody) {
    const basePath = priceRequestBody.is_rental
        ? types.RENTAL_BASEPATH
        : types.CAMP_BASEPATH;
    const reservationId = priceRequestBody.reservation_id;

    return axios.post(
        `${process.env.API}${basePath}/reservations/${reservationId}/price`,
        {
            reservation_id: priceRequestBody.reservation_id,
            campsite_id: priceRequestBody.campsite_id,
            check_in: priceRequestBody.start_date,
            check_out: priceRequestBody.end_date,
            reservation_options: priceRequestBody.reservation_options,
            resource_id: priceRequestBody.resource_id,
            quantity: priceRequestBody.quantity,
            site_occupant: priceRequestBody.site_occupant,
        },
        { headers: { Authorization: SecurityUtils.getAuthHeader() } }
    );
}

const constructPriceObj = (reservation, path, value) => {
    const pricingObj = {
        reservation_id: reservation.reservation_id,
        campsite_id: reservation.campsite_id,
        start_date: reservation.start_date,
        end_date: reservation.end_date,
        site_occupant: reservation.site_occupant,
        reservation_options: reservation.reservation_options,
        waive_fee_options: reservation.waive_fee_options,
        resource_id: reservation.resource_id,
        quantity: reservation.quantity,
        is_rental: !isNil(get(reservation, 'resource_id', null)),
    };

    // If current pending value is different from the new input value, set new input value
    if (!isEqual(get(pricingObj, path, null), value)) {
        set(pricingObj, path, value);
    }

    const processedSiteOccupant = cloneDeep(pricingObj.site_occupant);
    const processedEquipments = cloneDeep(processedSiteOccupant.selected_equipment);

    forEach(processedEquipments, (equip, index) => {
        if (isEqual(equip.length, '')) {
            processedEquipments[index].length = 0;
        }
    });
    set(processedSiteOccupant, 'selected_equipment', processedEquipments);
    const groupSize = get(processedSiteOccupant, 'custom_order_details.group_size', null);
    set(
        processedSiteOccupant,
        'num_occupants',
        orderDetailsUtil.getNumOccupants(groupSize)
    );
    const updatedNightMap = updateNightMapOccupants(reservation);

    set(
        pricingObj,
        'reservation_options.night_map',
        isEmpty(updatedNightMap) ? undefined : updatedNightMap
    );
    set(pricingObj, 'site_occupant', processedSiteOccupant);

    //supplemental selections
    const supplementalSelection = get(
        pricingObj,
        'reservation_options.supplemental_selection',
        {}
    );
    const processedSupplementalSelection = cleanSupplementalSelections(
        cloneDeep(supplementalSelection)
    );

    set(
        pricingObj,
        'reservation_options.supplemental_selection',
        isEmpty(processedSupplementalSelection)
            ? undefined
            : processedSupplementalSelection
    );

    return pricingObj;
};

/**
 *
 * @param {Observable} action$ - a stream of actions
 * @param {Observable} state$ - a stream of redux store states
 */
export const feeAdjustmentEpic = (action$, state$) =>
    action$.pipe(
        ofType(types.PENDING_RESERVATION_PROP_CHANGE_REQUEST), // Filter action type of "PENDING_RESERVATION_PROP_CHANGE_REQUEST"
        debounceTime(1000), // Proceed only if 0.5 sec has passed without another source Observable emission
        withLatestFrom(
            // Combines each value from the source Observable with the latest input Observable from `state$.internalCamping.pendingReservation`
            state$.pipe(pluck('orderDetails'), pluck('pendingReservations'))
        ),
        /* For each latest stream of emission from state$.internalCamping.pendingReservation after the debounce time period, apply the following function
        - input: action dispatched from setPendingReservationProp() action creator
        - state: state of the latest object from "internalCamping.pendingReservation"
    */
        mergeMap(([input, state]) => {
            const { reservationId, path, value } = input.payload;
            const reservation = get(state, reservationId, null);

            /* If pending reservation is:
            1) an itinerary reservation, or ...
            2) if the path to the target data is part of the list of paths that requires price update when modified
            3) if path is null (that mean the entire reservation object is being replaced)
        */
            const facilityType = get(
                reservation,
                'facility_type',
                STANDARD_TYPE_FACILITY
            );
            const basePathMatch = find(
                feeConstants.PATH_INVOLVING_FEE_CHANGE,
                (basePaths) => includes(path, basePaths)
            );

            if (
                includes(facilityType, ITINERARY_TYPE_FACILITY) ||
                !isNil(basePathMatch) ||
                isNil(path)
            ) {
                /* If reservation belongs to VOYA, update price for all reservations in itinerary,
            otherwise only update price for the reservation changed */
                const facilityId = get(reservation, 'facility_id', '');
                const reservationsToUpdate =
                    facilityId === '249981' ? state : [reservation];
                // Fetch updated price with updated pending reservation data
                const transformToPromise = (res) => {
                    const pricingObj = constructPriceObj(res, path, value);
                    return from(fetchReservationPriceDetails(pricingObj));
                };

                const processPrice = () => {
                    // For all reservations that requires updating, subscribe to their price fetch response stream
                    const allRes = lodashMap(reservationsToUpdate, (r) => r);
                    return of(...allRes).pipe(
                        mergeMap(transformToPromise),
                        // If successful response returns, dispatch GET_PENDING_RESERVATION_PRICE_DETAILS action
                        map((response) => {
                            const url = get(response, 'config.data', '');
                            const requestData = JSON.parse(url);
                            const resId = get(requestData, 'reservation_id', '');

                            return {
                                type: types.GET_PENDING_RESERVATION_PRICE_DETAILS,
                                payload: response.data,
                                id: resId,
                            };
                        }),
                        // If error, dispatch GET_PENDING_RESERVATION_PRICE_DETAILS_ERROR action
                        catchError((error) =>
                            of({
                                type: types.GET_PENDING_RESERVATION_PRICE_DETAILS_ERROR,
                                error: get(
                                    error,
                                    'response.data.error',
                                    get(error, 'error', 'Error:')
                                ),
                            })
                        )
                    );
                };
                const passType = get(reservation, 'site_occupant.pass.pass_type', null);
                if (passType && INTERAGENCY_PASSES.includes(passType)) {
                    const passNumber = get(
                        reservation,
                        'site_occupant.pass.pass_number',
                        null
                    );
                    if (passNumber && passNumber.length >= 9) {
                        return processPrice();
                    }
                } else {
                    return processPrice();
                }
            }
            // If update doesn't require price update, simple set the 'changed' flag to true
            return of({
                type: types.PENDING_RESERVATION_PROP_CHANGED,
                isPriceLoading: false,
            });
        })
    );
